<template>
    <page :busy="busy">
        <div v-if="!showTable">
            <div style="display: flex">
                <b-button @click="goBack" variant="primary" class="mx-1"> View Details </b-button>
                <date-range-picker
                    :range-options="predefinedRanges"
                    :value="dateRange"
                    :date-format="userUnicodeDateFormat"
                    @input="handleDaterangeChange"
                ></date-range-picker>
                <responsive-dropdown variant="primary" :items="bookingMethodDropdown" :icon="['fas', 'filter']">{{
                    bookingMethodDropdownLabel
                }}</responsive-dropdown>
                <responsive-dropdown variant="primary" :items="statusListDropdown" :icon="['fas', 'filter']">{{
                    statusListDropdownLabel
                }}</responsive-dropdown>
            </div>

            <card class="m-2">
                <template #header-content> Reservations Utilization By Source </template>
                <template #body>
                    <vue-apex-charts type="bar" height="400" :options="chartOptions" :series="series" />
                </template>
            </card>

            <card class="m-2">
                <template #header-content>
                    Reservation Utilization By Percentage ({{ bookingMethodDropdownLabel }})
                </template>
                <template #body>
                    <vue-apex-charts type="pie" height="400" :options="pieChartOptions" :series="pieSeries" />
                </template>
            </card>
        </div>

        <grid
            v-else
            ref="grid"
            :columnDefs="columnDefs"
            :fetchCallback="fetchCallback"
            :isAutoWidth="true"
            :getRowStyle="rowStyle"
            :isSearch="false"
            :buttonToolbarClass="['align-items-start flex-md-nowrap']"
            :defaultSort="sort"
            :defaultDesc="desc"
            class="h-100"
        >
            <b-button @click="goBack" variant="primary" class="mr-1"> View Chart </b-button>
            <date-range-picker
                :range-options="predefinedRanges"
                :value="dateRange"
                :date-format="userUnicodeDateFormat"
                @input="handleDaterangeChange"
            ></date-range-picker>
            <responsive-dropdown variant="primary" :items="bookingMethodDropdown" :icon="['fas', 'filter']">{{
                bookingMethodDropdownLabel
            }}</responsive-dropdown>
            <responsive-dropdown variant="primary" :items="statusListDropdown" :icon="['fas', 'filter']">{{
                statusListDropdownLabel
            }}</responsive-dropdown>
            <export-to-excel-button :href="excelHref" />
            <div class="merge-source-wrapper mb-0 subscribers-wrapper d-none d-lg-flex mr-1">
                <form-input
                    id="source"
                    @input="(value) => searchSubscribers(value)"
                    :value="subscriberSearch"
                    placeholder="Search by subscriber"
                    autocomplete="off"
                    class="w-100"
                />
                <div class="merge-guests-wrapper subscribers-list-wrapper" v-if="!!subscriberSearch && busy">
                    <div class="guest-item p-2 d-flex align-items-center justify-content-center">
                        <b-spinner variant="primary" :small="true" />
                    </div>
                </div>
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-else-if="subscribersList.length && subscriberSearch"
                >
                    <div
                        class="guest-item p-2"
                        :key="subscriber.id"
                        v-for="subscriber in subscribersList"
                        @click="selectSubscriber(subscriber.text, subscriber.value)"
                    >
                        <span>{{ subscriber.text }}</span>
                    </div>
                </div>
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-else-if="!subscribersList.length && !!subscriberSearch && !selectedSubscriber"
                >
                    <div class="p-2">
                        <span class="text-center">Nothing found</span>
                    </div>
                </div>
                <div v-else />
            </div>

            <div class="merge-source-wrapper mb-0 subscribers-wrapper d-none d-lg-flex mr-1">
                <form-input
                    id="source"
                    autocomplete="off"
                    placeholder="Search by guest"
                    @input="(value) => searchUsers(value)"
                    :value="userSearch"
                    class="w-100"
                />
                <div class="merge-guests-wrapper subscribers-list-wrapper" v-if="!!userSearch && busy">
                    <div class="guest-item p-2 d-flex align-items-center justify-content-center">
                        <b-spinner variant="primary" :small="true" />
                    </div>
                </div>
                <div class="merge-guests-wrapper subscribers-list-wrapper" v-else-if="usersList.length && userSearch">
                    <div
                        class="guest-item p-2"
                        :key="user.id"
                        v-for="user in usersList"
                        @click="selectUser(user.text, user.value)"
                    >
                        <span>{{ user.text }}</span>
                    </div>
                </div>
                <div
                    class="merge-guests-wrapper subscribers-list-wrapper"
                    v-else-if="!usersList.length && !!userSearch && !selectedUser"
                >
                    <div class="p-2">
                        <span class="text-center">Nothing found</span>
                    </div>
                </div>
                <div v-else />
            </div>

            <b-button-group class="d-lg-none d-block">
                <more-filters-dropdown>
                    <div class="merge-source-wrapper mb-0 subscribers-wrapper d-flex d-lg-none mr-1">
                        <form-input
                            id="source"
                            @input="(value) => searchSubscribers(value)"
                            :value="subscriberSearch"
                            placeholder="Search by subscriber"
                            autocomplete="off"
                            class="w-100"
                        />
                        <div class="merge-guests-wrapper subscribers-list-wrapper" v-if="!!subscriberSearch && busy">
                            <div class="guest-item p-2 d-flex align-items-center justify-content-center">
                                <b-spinner variant="primary" :small="true" />
                            </div>
                        </div>
                        <div
                            class="merge-guests-wrapper subscribers-list-wrapper"
                            v-else-if="subscribersList.length && subscriberSearch"
                        >
                            <div
                                class="guest-item p-2"
                                :key="subscriber.id"
                                v-for="subscriber in subscribersList"
                                @click="selectSubscriber(subscriber.text, subscriber.value)"
                            >
                                <span>{{ subscriber.text }}</span>
                            </div>
                        </div>
                        <div
                            class="merge-guests-wrapper subscribers-list-wrapper"
                            v-else-if="!subscribersList.length && !!subscriberSearch && !selectedSubscriber"
                        >
                            <div class="p-2">
                                <span class="text-center">Nothing found</span>
                            </div>
                        </div>
                        <div v-else />
                    </div>
                    <div class="merge-source-wrapper mb-0 subscribers-wrapper d-flex d-lg-none mr-1">
                        <form-input
                            id="source"
                            @input="(value) => searchUsers(value)"
                            :value="userSearch"
                            placeholder="Search by guest"
                            autocomplete="off"
                            class="w-100"
                        />
                        <div class="merge-guests-wrapper subscribers-list-wrapper" v-if="!!subscriberSearch && busy">
                            <div class="guest-item p-2 d-flex align-items-center justify-content-center">
                                <b-spinner variant="primary" :small="true" />
                            </div>
                        </div>

                        <div
                            class="merge-guests-wrapper subscribers-list-wrapper"
                            v-else-if="usersList.length && userSearch"
                        >
                            <div
                                class="guest-item p-2"
                                :key="user.id"
                                v-for="user in usersList"
                                @click="selectUser(user.text, user.value)"
                            >
                                <span>{{ user.text }}</span>
                            </div>
                        </div>
                        <div
                            class="merge-guests-wrapper subscribers-list-wrapper"
                            v-else-if="!usersList.length && !!userSearch && !selectedUser"
                        >
                            <div class="p-2">
                                <span class="text-center">Nothing found</span>
                            </div>
                        </div>
                        <div v-else />
                    </div>
                </more-filters-dropdown>
            </b-button-group>
        </grid>
    </page>
</template>

<script>
import { format } from 'date-fns';
import debounce from 'lodash/debounce';
import { mapGetters } from 'vuex';
import API from '../../../api';
import { STAYLIO_APP_STATISTICS_RESERVATION_ENDPOINT, STAYLIO_APP_STATISTICS_USER_ENDPOINT } from '../../../endpoints';
import Grid from '../../generic/grid/grid.vue';
import page from '../../generic/page/page.vue';
import FormInput from '../../generic/form-input/form-input.vue';
import SearchMultiselect from '../../generic/search-multiselect/search-multiselect.vue';
import ExportToExcelButton from '../../generic/export-to-excel-button/export-to-excel-button.vue';
import MoreFiltersDropdown from '../../generic/more-filters-dropdown/more-filters-dropdown.vue';
import DateRangePicker, {
    LAST_MONTH,
    LAST_WEEK,
    LAST_YEAR,
    nameToRange,
    THIS_MONTH,
    THIS_WEEK,
    THIS_YEAR,
    TODAY,
    YESTERDAY,
    FUTURE,
} from '../../generic/date-range-picker/date-range-picker.vue';
import CardGrid from '../../generic/card-grid/card-grid.vue';
import VueApexCharts from 'vue3-apexcharts';
import Card from '../../generic/card/card.vue';
import ResponsiveDropdown from '../../generic/responsive-dropdown/responsive-dropdown.vue';

export default {
    components: {
        page,
        Grid,
        DateRangePicker,
        FormInput,
        SearchMultiselect,
        ExportToExcelButton,
        MoreFiltersDropdown,
        VueApexCharts,
        Card,
        CardGrid,
        ResponsiveDropdown,
    },
    name: 'StaylioAppGuestsStatistic',
    data() {
        return {
            busy: false,
            dateRequestFormat: 'yyyy-MM-dd',
            rowStyle: null,
            subscribersList: [],
            usersList: [],
            selectedSubscriber: '',
            selectedUser: '',
            subscriberSearch: '',
            userSearch: '',
            offset: 0,
            limit: 20,
            sort: 'from_date',
            desc: false,
            dateRange: nameToRange(FUTURE),
            predefinedRanges: [
                LAST_MONTH,
                LAST_WEEK,
                LAST_YEAR,
                THIS_MONTH,
                THIS_WEEK,
                THIS_YEAR,
                TODAY,
                YESTERDAY,
                FUTURE,
            ],
            reservationData: null,
            filterByBookingMethod: '',
            bookingMethodList: [],
            showTable: true,
            statusList: [
                {
                    id: 'downloaded',
                    label: 'Downloaded App',
                },
                {
                    id: 'never_downloaded',
                    label: 'Never Downloaded App',
                },
            ],
            selectedStatus: '',
        };
    },
    computed: {
        ...mapGetters('header', ['userUnicodeDateFormat', 'userCurrencySymbol']),

        bookingMethodDropdown() {
            const list = this.bookingMethodList.map((item) => {
                return {
                    label: item.booking_method_name,
                    click: () => {
                        this.filterByBookingMethod = item.booking_method;
                        this.refresh();
                    },
                };
            });
            list.unshift({
                label: 'All Sources',
                click: () => {
                    this.filterByBookingMethod = '';
                    this.refresh();
                },
            });
            return list;
        },

        bookingMethodDropdownLabel() {
            return (
                this.bookingMethodList.find((item) => item.booking_method === this.filterByBookingMethod)
                    ?.booking_method_name || 'All Sources'
            );
        },

        statusListDropdown() {
            const list = this.statusList.map((item) => {
                return {
                    label: item.label,
                    click: () => {
                        this.selectedStatus = item.id;
                        this.refresh();
                    },
                };
            });
            list.unshift({
                label: 'All Statuses',
                click: () => {
                    this.selectedStatus = '';
                    this.refresh();
                },
            });
            return list;
        },

        statusListDropdownLabel() {
            return this.statusList.find((item) => item.id === this.selectedStatus)?.label || 'All Statuses';
        },

        chartOptions() {
            return {
                chart: {
                    type: 'bar',
                    width: '100%',
                },
                labels:
                    this.selectedStatus != ''
                        ? [this.statusListDropdownLabel]
                        : this.statusList.map((item) => item.label),
                legend: {
                    show: true,
                    position: 'bottom',
                },
            };
        },

        pieChartOptions() {
            return {
                chart: {
                    type: 'pie',
                    width: '100%',
                },
                labels: this.statusList.map((item) => item.label),
                legend: {
                    show: true,
                    position: 'bottom',
                },
            };
        },

        series() {
            const list = [];

            if (this.reservationData) {
                for (const source of this.bookingMethodList) {
                    const data = [];
                    if (this.selectedStatus == 'downloaded') {
                        data.push(
                            this.reservationData.downloaded?.find(
                                (item) => item.booking_method === source.booking_method
                            )?.count || 0
                        );
                    } else if (this.selectedStatus == 'never_downloaded') {
                        data.push(
                            this.reservationData.never_downloaded?.find(
                                (item) => item.booking_method === source.booking_method
                            )?.count || 0
                        );
                    } else {
                        data.push(
                            this.reservationData.downloaded?.find(
                                (item) => item.booking_method === source.booking_method
                            )?.count || 0,
                            this.reservationData.never_downloaded?.find(
                                (item) => item.booking_method === source.booking_method
                            )?.count || 0
                        );
                    }
                    list.push({
                        name: source.booking_method_name,
                        data: data,
                    });
                }
            }

            return list;
        },

        pieSeries() {
            let list = [];
            if (this.reservationData) {
                list = [
                    this.reservationData.downloaded?.reduce((acc, item) => acc + item.count, 0) || 0,
                    this.reservationData.never_downloaded?.reduce((acc, item) => acc + item.count, 0) || 0,
                ];
            }
            return list;
        },

            columnDefs() {
                return [
                    {
                        field: 'from_date',
                        headerName: 'From Date',
                        type: 'dateColumn',
                        minWidth: 150,
                    },
                    {
                        field: 'to_date',
                        headerName: 'To Date',
                        type: 'dateColumn',
                        minWidth: 150,
                    },
                    {
                        field: 'id',
                        headerName: 'Reservation Number',
                        minWidth: 200,
                    },
                    {
                        field: 'user.id',
                        headerName: 'Guest ID',
                        minWidth: 100,
                    },
                    {
                        field: 'user.email',
                        headerName: 'Guest Email',
                        minWidth: 175,
                    },
                    {
                        field: 'user.owner',
                        headerName: 'Owner ID',
                        minWidth: 100,
                    },
                    {
                        field: 'user.owner_email',
                        headerName: 'Owner Email',
                        minWidth: 175,
                    },
                    {
                        field: 'booking_method_name',
                        headerName: 'Source',
                        minWidth: 150,
                    },
                    {
                        field: 'user.downloaded_app',
                        headerName: 'Downloaded App',
                        type: 'yesNoColumn',
                        minWidth: 150,
                    },
                    {
                        field: 'user.app_installed',
                        headerName: 'App Installed',
                        type: 'yesNoColumn',
                        minWidth: 150,
                        sortable: false,
                    },
                    {
                        field: 'shares',
                        headerName: 'Staylio Shares',
                        minWidth: 100,
                        autoWidthExclude: true,
                    }
                ];
            },
            excelHref() {
                return `${STAYLIO_APP_STATISTICS_RESERVATION_ENDPOINT()}excel/?offset=${this.offset}&limit=${
                    this.limit
                }&sort=${this.sort}&desc=${this.desc}&user=${this.selectedUser}&subscriber=${
                    this.selectedSubscriber
                }&booking_method=${this.filterByBookingMethod}&status=${this.selectedStatus}&start_date=${
                    this.startDateRequest
                }&end_date=${this.endDateRequest}&status=${this.selectedStatus}`;
            },
            endDateRequest() {
                return format(this.dateRange.to, this.dateRequestFormat);
            },
            startDateRequest() {
                return format(this.dateRange.from, this.dateRequestFormat);
            },
            fetchCallback() {
                return ({ offset, limit, sort, desc, search }) => {
                    this.sort = sort;
                    if (this.sort === 'booking_method_name') {
                        this.sort = 'booking_method';
                    }
                    return API.get(`${STAYLIO_APP_STATISTICS_RESERVATION_ENDPOINT()}grid/`, {
                        params: {
                            offset,
                            limit,
                            sort: this.sort,
                            desc,
                            search,
                            start_date: this.startDateRequest,
                            end_date: this.endDateRequest,
                            user: this.selectedUser,
                            subscriber: this.selectedSubscriber,
                            booking_method: this.filterByBookingMethod,
                            status: this.selectedStatus,
                        },
                    });
                };
            },
            goBack() {
                return () => {
                    this.showTable = !this.showTable;
                    this.selectedUser = '';
                    this.selectedSubscriber = '';
                    if (this.showTable) {
                        this.refresh();
                    }
                };
            },
        },
        mounted() {
            this.rowStyle = (params) => {
                if (params.node.rowPinned) {
                    return { 'font-weight': 'bold' };
                }
            };
            Promise.all([
                this.reservationStats(),
                this.fetchBookingMethods(),
                this.fetchSubscribers(),
                this.fetchUsers(),
            ]);
        },
        methods: {
            onSearchInput: debounce(function () {
                this.refresh();
            }, 500),
            async fetchSubscribers() {
                try {
                    this.busy = true;
                    const { data } = await API.get(`${STAYLIO_APP_STATISTICS_USER_ENDPOINT()}subscribers/`, {
                        params: {
                            search: this.subscriberSearch,
                        },
                    });
                    this.subscribersList = data.map(({ user }) => ({
                        text: `${user.id} - ${user.email}`,
                        value: user.id,
                    }));
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch subscribers' });
                } finally {
                    this.busy = false;
                }
            },
            async fetchUsers() {
                try {
                    this.usersList = [];
                    this.busy = true;
                    const { data } = await API.get(`${STAYLIO_APP_STATISTICS_USER_ENDPOINT()}users/`, {
                        params: {
                            search: this.userSearch,
                        },
                    });
                    data.forEach((pair) => {
                        const user = {
                            text: `${pair.id} - ${pair.email}`,
                            value: pair.id,
                        };
                        this.usersList.push(user);
                    });
                } catch (e) {
                    this.errorToast({ text: 'Could not fetch users' });
                } finally {
                    this.busy = false;
                }
            },
            selectSubscriber(text, value) {
                this.subscriberSearch = text;
                this.selectedSubscriber = value;
                this.subscribersList = [];
                this.refresh();
            },
            selectUser(text, value) {
                this.userSearch = text;
                this.selectedUser = value;
                this.usersList = [];
                this.refresh();
            },
            searchSubscribers: debounce(function (value) {
                this.subscriberSearch = value;
                this.selectedSubscriber = '';
                if (value === '') {
                    this.refresh();
                } else {
                    this.fetchSubscribers();
                }
            }, 500),
            searchUsers: debounce(function (value) {
                this.userSearch = value;
                this.selectedUser = '';
                if (value === '') {
                    this.refresh();
                } else {
                    this.fetchUsers();
                }
            }, 500),
            handleDaterangeChange(value) {
                this.dateRange = { ...value };
                this.refresh();
            },
            refresh() {
                if (this.showTable) {
                    this.$refs.grid?.refreshDataSource();
                }
                this.reservationStats();
                this.fetchBookingMethods();
            },
            async reservationStats() {
                this.busy = true;
                const data = await API.get(`${STAYLIO_APP_STATISTICS_RESERVATION_ENDPOINT()}reservation-stats/`, {
                    params: {
                        start_date: this.startDateRequest,
                        end_date: this.endDateRequest,
                        user: this.selectedUser,
                        subscriber: this.selectedSubscriber,
                        booking_method: this.filterByBookingMethod,
                    },
                });
                this.reservationData = data.data;
                this.busy = false;
            },
            async fetchBookingMethods() {
                this.busy = true;
                const { data } = await API.get(`${STAYLIO_APP_STATISTICS_RESERVATION_ENDPOINT()}booking-methods/`, {
                    params: {
                        start_date: this.startDateRequest,
                        end_date: this.endDateRequest,
                    },
                });
                this.bookingMethodList = data;
                this.busy = false;
            },
        },
    };
</script>

<style lang="scss" scoped>
.subscribers-list-wrapper {
    top: 40px;
}
.subscribers-wrapper {
    min-width: 300px;
}
</style>
